import React, {
  useEffect,
  useState,
} from 'react';
import Style from './style';
import {
  Grid,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
} from '@mui/material';
import logo from '../CardView/logo';
import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  Constants,
  Interfaces,
} from '../../../config';
import {
  InfoOutlined,
  Loop,
  Save,
} from '@mui/icons-material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { DirectoryTree, } from '../DirectoryTree';
import LoadingComponent from '../../Loading';
import { NoDataV2, } from '../../NoDataV2';
import { useDSCalls, } from '../../../hooks';
import {
  StyledButton,
  StyledTextField,
} from '../../Reusable';

type Params = {
  oldName: string;
};

interface Props {
  schemaTypes: Array<string>;
}

function HDFSSource(props: Props) {
  const classes = Style();
  const location = useLocation();
  const navigate = useNavigate();
  const { oldName, } = useParams<Params>();
  const inEditMode = location.pathname.includes('edit');
  const [expandedElements, setExpandedElements,] = useState<Array<string>>([]);
  const [exploreState, setExploreState,] = useState(true);
  const { schemaTypes, } = props;
  const [state, setState,] = useState<Interfaces.BaseDataSource>({
    id: '',
    name: '',
    schemaType: schemaTypes[0],
    uri: '',
    type: 'HDFS',
  });
  const {
    dataSources,
    loading: loadingDS,
    // error: errorTest,
    editDataSource,
    addDataSource,
    testDataSource,
    dataSourceToEdit: dataSourceToEditHook,
    directories,
    loadingEdit,
    errorEdit,
    loadingTest,
    errorTest,
    loadingDirectories,
    errorDirectories,
    testNewDataSource,
    testExistingDataSource,
    fetchDirectories,
    setDataSourceToEdit,
    fetchNestedDirectory,
    setTestDataSource,
  } = useDSCalls();
  const dataSourceToEdit = dataSourceToEditHook as Interfaces.AzureDataSource | null;

  const getTypeFromName = (directory: Interfaces.Directory) => {
    if (directory.isDirectory) {
      return 'DIRECTORY';
    }
    const extension = directory.path.split('.').pop();
    if (extension === 'csv') {
      return 'CSV';
    }
    return 'AVRO';
  };

  const getTypeFromUri = (uri: string) => {
    const extension = uri.split('.').pop();
    if (extension === 'csv') {
      return 'CSV';
    } else if (extension === 'avro') {
      return 'AVRO';
    }
    return 'DIRECTORY';
  };

  const endpoint = inEditMode && dataSourceToEdit
    ? `data-sources/${dataSourceToEdit.id}/hdfs/dir`
    : Constants.api.endpoints.hdfsDirectories;

  const fetchDirectoriesHDFS = (uri: string | null) => {
    fetchDirectories({
      uri: uri,
    }, endpoint);
  };

  useEffect(() => {
    setTestDataSource(null);
    if (inEditMode) {
      if (dataSourceToEdit) {
        setState({
          ...state,
          id: dataSourceToEdit?.id,
          name: dataSourceToEdit?.name,
          type: dataSourceToEdit?.type,
          schemaType: dataSourceToEdit?.schemaType,
          uri: dataSourceToEdit?.uri,
        });
        fetchDirectoriesHDFS(dataSourceToEdit.uri === '' ? null : dataSourceToEdit.uri);
        setExploreState(false);
      } else {
        if (!dataSources) {return;}
        const dataSourceToChange = dataSources.find((item) => item.name === oldName) as Interfaces.BaseDataSource;
        setDataSourceToEdit(dataSourceToChange);
      }
    }
  }, [dataSourceToEdit, dataSources,]);

  const handleChange = (event: any) => {
    let property = event.target.name;
    let value = event.target.value;

    setState({
      ...state,
      [property]: value,
    });
  };

  const handleTestSuccess = () => {
    if (loadingTest) {
      return <CircularProgress
        color="inherit"
        style={{
          width: 11,
          height: 11,
        }}
      />;
    }
    if (testDataSource && !errorTest) {
      return <FiberManualRecordIcon style={{ fill: 'green', }} />;
    } else if (
      !testDataSource &&
      !errorTest
    ) {
      return <FiberManualRecordIcon style={{ fill: 'grey', }} />;
    } else if (
      errorTest
    ) {
      return <FiberManualRecordIcon style={{ fill: 'red', }} />;
    }
  };

  const onTestConnection = () => {
    inEditMode ? testExistingDataSource(state) : testNewDataSource(state);
  };

  const postSubmitAction = () => {navigate('/data-sources');};

  const handleSubmit = (event: any) => {
    event.preventDefault();
    if (inEditMode && oldName) {
      editDataSource(state, oldName, postSubmitAction);
    } else {
      addDataSource(state, postSubmitAction);
    }
  };

  if (loadingEdit) {
    return <LoadingComponent message={'Loading data source'}/>;
  }

  if (errorEdit) {
    return (
      <p>{errorEdit}</p>
    );
  }

  if (inEditMode && !dataSourceToEdit && !loadingDS) {
    return (
      <p>No data source was selected for edit mode</p>
    );
  }

  return (
    <form name="hdfsDataSource" onSubmit={handleSubmit}>
      <div className={classes.root}>
        <Grid container justifyContent='center'>
          <img
            src={logo.images.hdfs}
            alt="FTP"
            className={classes.img}
          />
        </Grid>
        <Grid container spacing={3} justifyContent='center'>
          <Grid item xs={6}>
            <StyledTextField
              id="name"
              name="name"
              label="Name"
              variant="outlined"
              fullWidth
              size="small"
              value={state.name}
              onChange={(event: any) => {
                handleChange(event);
              }}
            />
            <StyledTextField
              id="type"
              name="schemaType"
              variant="outlined"
              value={state.schemaType}
              fullWidth
              size="small"
              onChange={handleChange}
              select
              SelectProps={{
                native: true,
              }}
            >
              {schemaTypes.map((option: string) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </StyledTextField>
          </Grid>
          <Grid item xs={6}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '4px',
              }}
            >
              <StyledTextField
                InputProps={{
                  endAdornment: (
                    <InputAdornment position={'end'}>
                      <Tooltip title={'Reload the files'} placement="top">
                        <IconButton
                          disabled={exploreState}
                          color={'primary'}
                          edge={'end'}
                          onClick={() => {
                            fetchDirectoriesHDFS(state.uri === '' ? null : state.uri);
                            setState({
                              ...state,
                              schemaType: getTypeFromUri(state.uri),
                            });
                          }}
                        >
                          <Loop />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                id="uri"
                name="uri"
                label="Uri"
                variant="outlined"
                fullWidth
                size="small"
                value={state.uri}
                onChange={(event: any) => {
                  handleChange(event);
                }}
              />
              <Tooltip title="Uri must start with 'file:///'" placement="top">
                <InfoOutlined
                  fontSize="small"
                  style={{
                    marginLeft: 45,
                    marginTop: 3,
                  }}
                />
              </Tooltip>
            </div>
            {
              exploreState
                ? (
                  <Box className={classes.treeBoxEmpty}>
                    <StyledButton
                      disabled={state.uri === '' || state.uri === null}
                      onClick={() => {
                        fetchDirectoriesHDFS(state.uri === '' ? null : state.uri);
                        setExploreState(false);
                      }}
                      color='primary'
                      variant='contained'
                      className={classes.exploreButton}
                    >
                      Explore
                    </StyledButton>
                    {
                      (state.uri === '' || state.uri === null)
                      && <Typography display={'block'} style={{ color: '#949494', }} variant={'caption'}>
                        Uri is required
                      </Typography>
                    }
                  </Box>
                )
                : (
                  <Box  className={classes.treeBox}>
                    {loadingDirectories && directories.length === 0 && <LoadingComponent size={60} message={'Loading bucket'} />}
                    {
                      errorDirectories &&
                      <div style={{ textAlign: 'center', }}>
                        <NoDataV2 message={errorDirectories} />
                        <StyledButton
                          onClick={() => {
                            fetchDirectories({
                              uri: state.uri,
                            }, Constants.api.endpoints.hdfsDirectories);
                            setExploreState(false);
                          }}
                          color='primary'
                          variant='contained'
                        >
                          Retry
                        </StyledButton>
                      </div>
                    }
                    {directories.length > 0 && !errorDirectories && (
                      <DirectoryTree
                        directories={directories}
                        isLoading={loadingDirectories}
                        expandedElements={expandedElements}
                        onTreeItemClick={(directory: Interfaces.Directory) => {
                          setState({
                            ...state,
                            uri: directory.path,
                            schemaType: getTypeFromName(directory),
                          });
                          if (directory.isDirectory) {
                            if (!directory.directories) {
                              fetchNestedDirectory({
                                uri: directory.path,
                              }, endpoint, directory.path);
                            }
                            if (expandedElements.includes(directory.path)) {
                              setExpandedElements([...expandedElements,].filter((expanded: string) => expanded !== directory.path));
                            } else {
                              setExpandedElements([...expandedElements, directory.path,]);
                            }
                          }
                        }}/>
                    )}
                  </Box>
                )
            }
          </Grid>
        </Grid>
        <div style={{ width: '100%', }}>
          <StyledButton
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            style={{ marginLeft: 0, }}
            startIcon={loadingDS ?
              <CircularProgress
                color="inherit"
                style={{
                  width: 16,
                  height: 11,
                }} />
              :
              <Save />}
          >
            Save
          </StyledButton>
          <StyledButton
            variant="outlined"
            size="large"
            className={classes.button}
            onClick={() => {
              navigate(-1);
            }}
          >
            Cancel
          </StyledButton>
          <StyledButton
            variant="contained"
            color="primary"
            size="large"
            style={{ float: 'right', }}
            startIcon={handleTestSuccess()}
            onClick={() => {
              onTestConnection();
            }}
          >
            Test
          </StyledButton>
        </div>
      </div>
    </form>
  );
}

export default HDFSSource;
